import axios from 'axios';
import { Cookies } from 'react-cookie';
import { URL_FOUNDATION } from '../../../config/keys';

// Shelf vision images of a product (frontend/vision): the server stores the JPEGs and their
// rows; phones embed new packshots at their next sync and drop deleted ones. Same two-stage
// upload as the file manager: presigned S3 PUT, then registration with the size check.

const cookies = new Cookies();
const token = cookies.get('id_token') || null;
axios.defaults.headers.common.Authorization = `${token}`;

const VISION = `${URL_FOUNDATION}/api/v2/vision`;

export type VisionImageKind = 'packshot' | 'field';

export interface VisionImage {
	id: string
	sku_id: string
	kind: VisionImageKind
	/** Stored format: `jpg` (phones) or `webp` (converted packshots). */
	ext?: 'jpg' | 'webp'
	bytes: number
	width: number
	height: number
	created_at?: string | null
	updated_at?: string | null
	deleted_at?: string | null
	/** Presigned download URL, on live rows. */
	url?: string | null
}

interface VisionPage<T> {
	server_time: string
	items: T[]
}

export async function getVisionImages(skuId: string): Promise<VisionImage[]> {
	return axios.get<VisionPage<VisionImage>>(`${VISION}/images`, { params: { sku_id: skuId, limit: 5000 } }).then(res => res.data.items);
}

export async function deleteVisionImage(id: string): Promise<void> {
	await axios.delete(`${VISION}/images/${encodeURIComponent(id)}`);
}

/** Same shape as the app's image ids: sortable stamp + uuid, unique across devices. */
export function newVisionImageId(): string {
	return `${new Date().toISOString().replace(/:/g, '-')}-${crypto.randomUUID()}`;
}

/**
 * Any brand file (TIFF, PNG, JPEG, SVG, WebP, BMP, GIF) → lossless WebP, at most `maxSide` px
 * on the longest side. The conversion is the Rust `packshot` crate compiled to wasm
 * (backend/lib/webassembly/packshot-web), loaded on first use; phones and the gallery builder
 * decode exactly the bytes stored.
 */
export async function toLosslessWebp(file: File, maxSide = 2048): Promise<{ blob: Blob, width: number, height: number, source: string }> {
	const { to_lossless_webp } = await import('packshot-web');
	const bytes = new Uint8Array(await file.arrayBuffer());
	const converted = to_lossless_webp(bytes, maxSide);
	try {
		return { blob: new Blob([converted.webp], { type: 'image/webp' }), width: converted.width, height: converted.height, source: converted.source };
	} finally {
		converted.free();
	}
}

/** Upload one packshot for a product (its uuid is the vision reference id), as lossless WebP. */
export async function uploadPackshot(skuId: string, file: File): Promise<VisionImage> {
	const { blob, width, height } = await toLosslessWebp(file);
	const id = newVisionImageId();
	const stage1 = await axios.get<{ url: string, key: string }>(`${VISION}/images/upload`, { params: { id, ext: 'webp' } }).then(res => res.data);
	// plain fetch: the global Authorization header must not reach S3
	const put = await fetch(stage1.url, { method: 'PUT', mode: 'cors', body: blob, headers: { 'Content-Type': 'image/webp' } });
	if (!put.ok) throw new Error(`S3 upload failed (${put.status})`);
	const image: VisionImage = { id, sku_id: skuId, kind: 'packshot', ext: 'webp', bytes: blob.size, width, height, created_at: new Date().toISOString() };
	await axios.post(`${VISION}/images`, image);
	return image;
}
